import { type BranchKey, STANDARD_BRANCH } from '@festina-life/api'
import {
  useAuthenticationContext,
  useGetUserRoles,
} from '@festina-life/authentication'
import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useFetchStandardComponentTenants } from '../api/useFetchStandardComponentTenants'
import { useFetchConfigurationTenants } from './useFetchConfigurationTenants'

type Props = { branchKey: Optional<BranchKey>; block?: Optional<string> }

export const useAllowedTenants = ({ branchKey, block }: Props) => {
  const { data: roles } = useGetUserRoles()
  const { shouldValidateAuthenticationHooks } = useAuthenticationContext()

  const isUsingTheConfigurationServer =
    !!branchKey?.name && branchKey.name !== STANDARD_BRANCH

  const standardComponentTenants = useFetchStandardComponentTenants({
    server: branchKey?.server,
    enabled: !isUsingTheConfigurationServer,
  })
  const configurationComponentTenants = useFetchConfigurationTenants({
    enabled: isUsingTheConfigurationServer && !!block,
    branchKey,
    block,
  })

  return useMemo(() => {
    const serverRoles = branchKey
      ? roles[
          isUsingTheConfigurationServer ? 'configuration' : branchKey.server
        ]
      : []

    const { data, ...rest } = isUsingTheConfigurationServer
      ? configurationComponentTenants
      : standardComponentTenants

    const allowedTenants = data?.filter((tenant) =>
      shouldValidateAuthenticationHooks
        ? serverRoles?.includes(`${tenant.tenantId}.T`)
        : true,
    )

    return {
      data: {
        tenants: allowedTenants,
      },
      ...rest,
    }
  }, [
    branchKey,
    roles,
    isUsingTheConfigurationServer,
    configurationComponentTenants,
    standardComponentTenants,
    shouldValidateAuthenticationHooks,
  ])
}
